import React, { useMemo, useState } from "react";
import { Autocomplete, Box, TextField, Typography, alpha, createFilterOptions } from "@mui/material";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { scorePercent } from "../AuditHub/utils";
import { AH_ACCENT, AH_MUTED, AH_TEXT } from "../AuditHub/theme";
import { accountAvailability } from "./AuditContextBar";
import { scopeLabel } from "./ControlAudit/ApplyToScopesDialog";

const filterOptions = createFilterOptions({ stringify: (option) => option.search });

const fieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    py: "0 !important",
    pl: "6px !important",
    minHeight: 38,
    fontSize: "0.8125rem",
    backgroundColor: alpha(AH_ACCENT, 0.06),
    borderRadius: "8px",
    transition: "box-shadow 0.15s ease, background-color 0.15s ease",
    "& fieldset": { borderColor: alpha(AH_ACCENT, 0.55), borderWidth: "1.5px" },
    "&:hover": { backgroundColor: alpha(AH_ACCENT, 0.1) },
    "&:hover fieldset": { borderColor: AH_ACCENT },
    "&.Mui-focused": { boxShadow: `0 0 0 3px ${alpha(AH_ACCENT, 0.18)}` },
    "&.Mui-focused fieldset": { borderColor: AH_ACCENT, borderWidth: "1.5px" },
  },
  "& .MuiAutocomplete-input": {
    py: "7px !important",
    fontWeight: 600,
    color: AH_TEXT,
    textOverflow: "ellipsis",
  },
  "& .MuiAutocomplete-popupIndicator": { color: AH_ACCENT },
};

const iconTileSx = {
  width: 26,
  height: 26,
  borderRadius: "6px",
  backgroundColor: alpha(AH_ACCENT, 0.14),
  color: AH_ACCENT,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  mr: 0.5,
};

const listboxSx = {
  maxHeight: 320,
  "& .MuiAutocomplete-option[aria-selected='true']": { backgroundColor: alpha(AH_ACCENT, 0.1) },
  "& .MuiAutocomplete-option[aria-selected='true'].Mui-focused": { backgroundColor: alpha(AH_ACCENT, 0.16) },
};

/** Searchable picker for the audit's accounts on the current framework; scales to any number of accounts. */
const AuditAccountSwitcher = ({ accounts, compliance, accountNumber, onChange }) => {
  const options = useMemo(
    () =>
      (Array.isArray(accounts) ? accounts : []).map((account) => {
        const { scope, reason } = accountAvailability(account, compliance);
        return {
          accountNumber: account.accountNumber,
          label: scopeLabel(account),
          search: `${account.accountName || ""} ${account.accountNumber || ""}`,
          percent: scope?.hasScanResult ? scorePercent(scope.auditScore) : null,
          reason,
        };
      }),
    [accounts, compliance]
  );
  const value = options.find((option) => option.accountNumber === accountNumber) || null;
  // Closed, the field shows the selected account; open, it is an empty search box so nothing is pre-selected.
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  return (
    <Box sx={{ display: "flex", minWidth: 0, width: { xs: "100%", sm: 340 } }}>
      <Autocomplete
        id="audit-account-switcher"
        size="small"
        options={options}
        value={value}
        onChange={(_, option) => {
          if (option) onChange(option.accountNumber);
        }}
        open={open}
        onOpen={() => {
          setQuery("");
          setOpen(true);
        }}
        onClose={() => setOpen(false)}
        inputValue={open ? query : value?.label || ""}
        onInputChange={(_, next, reason) => {
          if (reason === "input") setQuery(next);
        }}
        selectOnFocus={false}
        getOptionLabel={(option) => option.label}
        getOptionDisabled={(option) => Boolean(option.reason)}
        isOptionEqualToValue={(option, selected) => option.accountNumber === selected.accountNumber}
        filterOptions={filterOptions}
        disableClearable
        autoHighlight
        openOnFocus
        popupIcon={<KeyboardArrowDownRoundedIcon />}
        noOptionsText="No matching accounts"
        ListboxProps={{ sx: listboxSx }}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <li key={key} {...optionProps}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%", minWidth: 0 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: "0.8125rem", color: AH_TEXT, overflowWrap: "anywhere" }}>{option.label}</Typography>
                  {option.reason ? <Typography sx={{ fontSize: 12, color: AH_MUTED }}>{option.reason}</Typography> : null}
                </Box>
                {option.percent != null ? (
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: AH_TEXT, flexShrink: 0 }}>
                    {Math.round(option.percent)}%
                  </Typography>
                ) : null}
              </Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search accounts"
            inputProps={{ ...params.inputProps, "aria-label": "Viewing account", title: open ? undefined : value?.label }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <Box aria-hidden sx={iconTileSx}>
                    <SwapHorizRoundedIcon sx={{ fontSize: 18 }} />
                  </Box>
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
        sx={fieldSx}
      />
    </Box>
  );
};

export default AuditAccountSwitcher;
